import { SPECIAL_TASKS, specialOffers, titleCase, type BuilderNode, type BuilderNodePatch, type BuilderTree, type SpecialKey } from '@nsb/core';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../ui.tsx';
import { stillChoosing } from './specialChoice.ts';

/** A step named in a line of words: quoted, in Title Case, as the card names it. */
const q = (t: string) => `“${titleCase(t)}”`;

/**
 * A step's special tasks on its card (spec "Pattern Mapping" §3.2; Jason, 2026-10-15: "in customizing or setting up a
 * node has a general tool for selecting from among a set of available custom tasks"): the ones it carries, each with
 * what it does, what is set for it at project setup and what finishes it, and Take off; then one quiet Add a special
 * task, only while there is one it could take now (core `specialOffers`: its kind may carry it and no other step of the
 * tree does), opening the choice of them, each a row with its name, what it does and an Add, the whole row its target. Nothing at all when it carries none
 * and can take none (Jason, 2026-10-17: "it's silly that every card had a pattern mapping special task, it should be a
 * generic add and give that as an optional special task, there really is only one pattern mapping step after all").
 * `startOpen` is for tests, to read the choice open.
 */
export function SpecialTasks({
  tree,
  node,
  set,
  startOpen = false,
}: {
  tree: BuilderTree;
  node: BuilderNode;
  set: (patch: BuilderNodePatch, words: string) => void;
  startOpen?: boolean;
}) {
  const { carried, addable } = specialOffers(tree, node);
  const [choosing, setChoosing] = useState(startOpen);
  const listId = useId();
  const opener = useRef<HTMLButtonElement>(null);
  const first = useRef<HTMLButtonElement>(null);
  const open = stillChoosing(choosing, addable);
  // Set back while rendering, as React has state follow what it is drawn from: no effect, no frame drawn open.
  if (choosing && !open) setChoosing(false);
  // Opened, the first to choose has the focus; closed with Escape or Cancel, the button that opened it has it back.
  useEffect(() => {
    if (open && !startOpen) first.current?.focus();
  }, [open, startOpen]);
  if (!carried.length && !addable.length) return null;
  const close = () => {
    setChoosing(false);
    opener.current?.focus();
  };
  const add = (k: SpecialKey) => {
    setChoosing(false);
    set({ special: [...carried, k] }, `${q(node.title)} carries ${SPECIAL_TASKS[k].name} now.`);
  };
  return (
    <section className="mt-4">
      {carried.length ? (
        <>
          <h3 className="label mb-1.5">Special tasks</h3>
          <ul className="border-t border-rule">
            {carried.map((k) => {
              const s = SPECIAL_TASKS[k];
              return (
                <li key={k} className="flex items-start gap-3 border-b border-rule py-2.5">
                  <div className="min-w-0 flex-1">
                    <p>{s.name}</p>
                    <p className="meta mt-0.5">{s.does}</p>
                    {s.setAtSetup ? <p className="meta mt-0.5">Set at project setup: {s.setAtSetup}</p> : null}
                    <p className="mt-0.5 text-[0.8125rem]">Done when: {s.doneWhen}</p>
                  </div>
                  <Button
                    tone="danger"
                    aria-label={`Take ${s.name} off this step`}
                    onClick={() => set({ special: carried.filter((x) => x !== k) }, `${q(node.title)} no longer carries ${s.name}.`)}
                  >
                    Take off
                  </Button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      {addable.length ? (
        <div
          onKeyDown={(e) => {
            // Escape closes the choice alone, never the step's card behind it.
            if (open && e.key === 'Escape') {
              e.stopPropagation();
              close();
            }
          }}
        >
          <Button ref={opener} tone="quiet" aria-expanded={open} aria-controls={listId} onClick={() => (open ? close() : setChoosing(true))}>
            Add a special task
          </Button>
          {open ? (
            <div id={listId} role="group" aria-label="Special tasks this step could carry">
              <ul className="border-t border-rule">
                {addable.map((k, i) => {
                  const s = SPECIAL_TASKS[k];
                  const id = `${listId}-${k}`;
                  return (
                    <li key={k} className="border-b border-rule">
                      <button
                        ref={i === 0 ? first : undefined}
                        type="button"
                        aria-label={`Add ${s.name} to this step`}
                        aria-describedby={s.setAtSetup ? `${id}-does ${id}-setup` : `${id}-does`}
                        onClick={() => add(k)}
                        className="group -mx-2 flex min-h-11 w-[calc(100%+1rem)] items-start gap-3 px-2 py-2.5 text-left transition-colors duration-[140ms] ease-drafting hover:bg-paper motion-reduce:transition-none"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block">{s.name}</span>
                          <span id={`${id}-does`} className="meta mt-0.5 block">
                            {s.does}
                          </span>
                          {s.setAtSetup ? (
                            <span id={`${id}-setup`} className="meta mt-0.5 block">
                              Set at project setup: {s.setAtSetup}
                            </span>
                          ) : null}
                        </span>
                        <span aria-hidden="true" className="link shrink-0 text-ink-2 group-hover:text-ink group-hover:decoration-current">
                          Add
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <Button tone="quiet" onClick={close}>
                Cancel
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
